/**
 * Collapsed-panel RAIL (APP-3397 / APP-3413): a reserved-width bordered column that stands in for the docked
 * conversations panel when it is collapsed. It is a normal flex child (like ChatSidePanel), NOT a floating
 * overlay, so the chat + artifact region lives entirely to its right and can never be underlapped. The rail
 * holds two top-aligned controls: an Expand button that re-docks the full panel (the click/keyboard path to
 * the list, and the caller's focus target), and a New-chat (+) button that starts a thread. Hovering the +
 * button opens the shared select-only peek (`conversations-preview.tsx`) — quick-jump only. Selecting a row
 * navigates (opening/focusing its tab via the controller) and dismisses the preview WITHOUT re-docking; the
 * Expand button is the explicit re-dock.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { Ref } from "react"
import { Add, ExtensionOutlined, ViewSidebarOutlined } from "@mui/icons-material"
import { IconButton, Paper, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import { ConversationsPreview } from "./conversations-preview"
import { useKellyPanel } from "./kelly-chat-context"
import type { SkillsEntryProps } from "./side-panel"
import { useHoverPreview } from "./use-hover-preview"

// A reserved-width column in the panel's border/paper language (mirrors ChatSidePanel's right border). Top
// padding matches the panel's COLLAPSE_ROW (p:0.5) so the Expand button reads as the collapse toggle swapping sides.
const RAIL_SX = { flexShrink: 0, width: (theme: Theme) => theme.spacing(6.5), borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, p: 0.5, minHeight: 0 } satisfies SxProps<Theme>
// Rail buttons read as quiet navigation; New chat is the only primary action on the rail.
const RAIL_BUTTON_SX = { color: "var(--app-text-secondary)", "&:hover": { color: "var(--app-text-primary)", backgroundColor: "var(--app-subtle-hover-surface)" } } satisfies SxProps<Theme>
const SKILLS_BUTTON_OPEN_SX = { ...RAIL_BUTTON_SX, bgcolor: "action.selected" } satisfies SxProps<Theme>

interface CollapsedRailProps {
  /** Re-dock (expand) the panel. */
  onOpen: () => void
  /** Focus target so a collapse lands focus on the Expand button (mirrors the panel's collapse-button focus). */
  openButtonRef?: Ref<HTMLButtonElement>
  /** The Skills page entry, when this surface has one; the same entry the expanded panel lists. */
  skills?: SkillsEntryProps
}

export function CollapsedRail({ onOpen, openButtonRef, skills }: CollapsedRailProps) {
  const { startNewThread } = useKellyPanel()
  const { isOpen, surfaceRef, closeNow, triggerHandlers, surfaceHandlers } = useHoverPreview()
  // The + button is both the hover trigger and the Popper anchor; anchor is state so it re-renders when the
  // node mounts (reading a plain ref during render for anchorEl is forbidden — react-hooks/refs).
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleNewChat = useCallback(() => {
    closeNow()
    startNewThread()
  }, [closeNow, startNewThread])

  // Track only where the preview goes from closed to open: useHoverPreview's `open` fires again when the
  // pointer returns during the close delay, which would count one continuous hover as several.
  const analytics = useAnalytics()
  const onOpenSkills = skills?.onOpen
  const isSkillsOpen = skills?.isOpen ?? false
  const openSkills = useCallback(() => {
    closeNow()
    if (!isSkillsOpen) analytics.track(SKILL_EVENTS.SKILLS_PAGE_OPENED, { surface: "collapsed_rail" })
    onOpenSkills?.()
  }, [closeNow, analytics, isSkillsOpen, onOpenSkills])
  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) analytics.track(AI_TEAMMATE_EVENTS.RECENTS_PREVIEW_OPENED, { surface: "collapsed_rail" })
    wasOpenRef.current = isOpen
  }, [isOpen, analytics])

  return (
    <Paper square elevation={0} sx={RAIL_SX} component="aside" aria-label="Collapsed conversations panel">
      {/* disableHoverListener: hover belongs to the preview Popper below — a hover tooltip opens in the same
          spot after its delay, sits in the pointer path, and fires the preview's leave handlers so the grace
          timer dismisses the list. Focus/touch still surface the label for keyboard users (no preview then). */}
      <Tooltip title="Open conversations" disableHoverListener>
        <IconButton
          ref={openButtonRef}
          aria-label="Open conversations panel"
          data-testid="chatbox-rail-expand"
          sx={RAIL_BUTTON_SX}
          onClick={onOpen}
        >
          <ViewSidebarOutlined />
        </IconButton>
      </Tooltip>
      {skills && (
        <Tooltip title="Skills">
          <IconButton
            aria-label="Skills"
            aria-current={skills.isOpen ? "page" : undefined}
            data-testid="chatbox-rail-skills"
            sx={skills.isOpen ? SKILLS_BUTTON_OPEN_SX : RAIL_BUTTON_SX}
            onClick={openSkills}
          >
            <ExtensionOutlined />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="New chat">
        <IconButton
          ref={setAnchorEl}
          aria-label="New chat"
          data-testid="chatbox-rail-new-chat"
          color="primary"
          onClick={handleNewChat}
          {...triggerHandlers}
        >
          <Add />
        </IconButton>
      </Tooltip>
      <ConversationsPreview
        isOpen={isOpen}
        anchorEl={anchorEl}
        surfaceRef={surfaceRef}
        surfaceHandlers={surfaceHandlers}
        onDismiss={closeNow}
        testId="chatbox-rail-preview"
      />
    </Paper>
  )
}
